import React, {useEffect} from 'react';
import {useSetRecoilState} from 'recoil';
import {authLoadingState, userState} from '../states/user/userAtom';
import {
    getNewAuthTokens,
    me,
    resetClientAuthToken,
    setClientAccessToken,
    setClientAuthToken
} from '../api/AlfyApiClient';
import {ACCESS_TOKEN_LOCAL_STORAGE_KEY} from "../constants/constants";
import {JwtUtils} from "../utils/JwtUtils";
import ReactGA from "react-ga4"

const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const setUser = useSetRecoilState(userState);
    const setAuthLoading = useSetRecoilState(authLoadingState);
    const [isInitialized, setIsInitialized] = React.useState(false);
    const currentPath = `${window.location.pathname}`

    ReactGA.initialize("G-FH6S55PYG4")

    const initializeAuth = async () => {
        setAuthLoading(true);
        const authToken = await JwtUtils.getTokenFromLocalStorage(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
        if (authToken === null) {
            // localStorage에서 가져온 토큰이 유효하지 않은 경우 새 토큰 발행 요청
            const accessTokenAndRefreshToken = await getNewAuthTokens();
            if (accessTokenAndRefreshToken === null) {
                resetClientAuthToken();
                setAuthLoading(false);
                setIsInitialized(true); // 초기화 완료
                return
            } else {
                setClientAuthToken(accessTokenAndRefreshToken)
            }
        } else {
            // localStorage에서 가져온 토큰이 유효한 경우 그대로 axios 기본 헤더에 설정
            setClientAccessToken(authToken);
        }

        try {
            const fetchedUser = await me();
            if (fetchedUser) {
                setUser(fetchedUser);
            }
        } catch (error) {
            console.error("failed to fetch user", error);
        } finally {
            setAuthLoading(false);
            setIsInitialized(true); // 초기화 완료
        }
    };

    useEffect(() => {
        function updateViewportHeight() {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        }

        updateViewportHeight(); // 초기 실행
        window.addEventListener('resize', updateViewportHeight);

        return () => {
            window.removeEventListener('resize', updateViewportHeight); // 언마운트 시 이벤트 제거
        };
    }, []);

    useEffect(() => {
        if (currentPath === '/oauth') {
            setIsInitialized(true); // 카카오 로그인 콜백 페이지인 경우 초기화하지 않음
            return;
        }

        initializeAuth();
    }, []);

    if (!isInitialized) {
        // 초기화 완료 전까지 로딩 화면 표시
        return <div>Loading application...</div>;
    }

    return <>{children}</>; // 초기화 완료 후 자식 컴포넌트 렌더링
};

export default AppInitializer;
